import type { MenuSection } from '@1js/acui-menu/lib/components/MenuDefinition';
import type { MenuItemControlProps } from '@1js/acui-ribbon-like/lib/UISurfaces/controls/Menu';
import { constructKeytip, getKeytipString } from 'owa-command-ribbon';
import { getComposeRibbonId } from 'owa-editor/lib/utils/getComposeRibbonId';
import { getMainWindowComposeViewState } from 'owa-mail-compose-actions/lib/selectors/getMainWindowComposeViewState';
import type { ComposeViewState } from 'owa-mail-compose-store';
import shouldMarkCLPLabelAsChecked from 'owa-mail-compose-view/lib/utils/shouldMarkCLPLabelAsChecked';
import type CLPLabel from 'owa-mail-protection-types/lib/schema/CLPLabel';
import { protectionStore } from 'owa-mail-protection/lib/store/protectionStore';
import type { RibbonControlDefinitionProps } from 'owa-mail-ribbon-utils';
import { ControlInfo, getControlInfo } from 'owa-mail-ribbon-utils';
import { MailRibbonControlId, MailRibbonTabId } from 'owa-ribbon-ids';
import isSmimeEnabledInViewState from 'owa-smime/lib/utils/isSmimeEnabledInViewState';
import { onSensitivityClicked } from '../../util/commandActions';
import {
    createMailAppButton,
    createMailGeneratedMenuFlyoutAnchor,
    createMailGeneratedMenuItem,
} from '../../util/createMailRibbonButton';

export default function getSensitivitySections(props: RibbonControlDefinitionProps): MenuSection[] {
    const sensitivityDropdownSections: MenuSection[] = [];
    const { clpLabels } = protectionStore;
    const viewState: ComposeViewState | undefined =
        props.popoutComposeViewState ?? getMainWindowComposeViewState();

    if (viewState !== undefined && clpLabels?.length > 0) {
        const { protectionViewState } = viewState;
        const { clpViewState } = protectionViewState;

        sensitivityDropdownSections.push({
            controls: getSensitivityControls(
                clpLabels,
                clpViewState.selectedCLPLabel,
                isSmimeEnabledInViewState(viewState.smimeViewState),
                props
            ),
        });
    }

    sensitivityDropdownSections.push({ controls: getSensitivityLearnMore() });

    return sensitivityDropdownSections;
}

function getSensitivityControls(
    clpLabels: CLPLabel[],
    selectedCLPLabel: CLPLabel,
    isSmimeEnabled: boolean,
    props: RibbonControlDefinitionProps,
    parent?: string
): MenuItemControlProps[] {
    const controls: MenuItemControlProps[] = [];
    const parentSubstring: string = (parent ? parent : '') + '_';

    clpLabels
        .filter(label => label.isLabelEnabled)
        .forEach(label => {
            if (label.children && label.children.some(childLabel => childLabel.isLabelEnabled)) {
                controls.push(
                    createMailGeneratedMenuFlyoutAnchor(
                        getComposeRibbonId(
                            'sensitivity' + parentSubstring + label.displayName,
                            props.popoutComposeViewState
                        ),
                        label.displayName,
                        undefined /*tooltip*/,
                        {
                            sections: [
                                {
                                    controls: getSensitivityControls(
                                        label.children,
                                        selectedCLPLabel,
                                        isSmimeEnabled,
                                        props,
                                        parentSubstring + label.displayName
                                    ),
                                },
                            ],
                        },
                        undefined /* keytipProps */,
                        shouldMarkCLPLabelAsChecked(label, selectedCLPLabel),
                        undefined /*icon*/,
                        undefined /*iconColor*/,
                        undefined /*styles*/,
                        { disabled: isSmimeEnabled && label.isEncryptingLabel }
                    )
                );

                return;
            }

            controls.push(
                createMailGeneratedMenuItem(
                    getComposeRibbonId(
                        'sensitivity' + parentSubstring + label.displayName,
                        props.popoutComposeViewState
                    ),
                    label.displayName,
                    label.tooltip,
                    () => {
                        onSensitivityClicked(
                            label,
                            props.popoutComposeViewState ?? getMainWindowComposeViewState()!,
                            props.targetWindow
                        );
                    },
                    undefined /* keytipProps */,
                    shouldMarkCLPLabelAsChecked(label, selectedCLPLabel),
                    undefined /*icon*/,
                    undefined /*iconColor*/,
                    undefined /*styles*/,
                    { disabled: isSmimeEnabled && label.isEncryptingLabel }
                )
            );
        });

    return controls;
}

function getSensitivityLearnMore(): MenuItemControlProps[] {
    const controls: MenuItemControlProps[] = [];
    const { learnMoreUrl } = protectionStore;

    if (learnMoreUrl) {
        const controlInfo: ControlInfo = getControlInfo(MailRibbonControlId.Sensitivity_LearnMore);

        controls.push(
            createMailAppButton(
                MailRibbonControlId.Sensitivity_LearnMore,
                controlInfo.text,
                controlInfo.tooltip,
                false /*isPopout*/,
                () => {
                    window.open(learnMoreUrl, '_blank');
                },
                constructKeytip(
                    [
                        getKeytipString(MailRibbonTabId.Tab_Message),
                        getKeytipString(MailRibbonControlId.Sensitivity),
                    ],
                    getKeytipString(MailRibbonControlId.Sensitivity_LearnMore)
                ),
                controlInfo.iconName
            )
        );
    }

    return controls;
}
